import { useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const t = localStorage.getItem('token') || localStorage.getItem('access_token') || '';
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/**
 * useContinueWork — quietly fetches the user's actual in-progress items
 * from three existing backends (Case Locker, Grievance Builder, Fact Finder).
 * Returns up to 4 items sorted by most-recent updatedAt.  Never fabricates
 * data — if the endpoints fail we return an empty array.
 */
export default function useContinueWork() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const collected = [];

      // Case Locker open cases
      try {
        const r = await axios.get(`${API}/api/case-locker/cases`, { headers: authHeaders(), params: { limit: 3 } });
        const cases = r?.data?.cases || (Array.isArray(r?.data) ? r.data : []);
        cases.slice(0, 3).forEach((c) => {
          const id = c.case_id || c.id || c._id;
          if (!id) return;
          collected.push({
            id: `case-${id}`,
            type: 'case',
            title: c.title || 'Untitled case',
            status: c.status || 'Open',
            updatedAt: c.updated_at || c.created_at,
            route: `/case-locker/${id}`,
          });
        });
      } catch { /* silent */ }

      // Grievance drafts
      try {
        const r = await axios.get(`${API}/api/grievance-builder/drafts`, { headers: authHeaders(), params: { limit: 2 } });
        const drafts = r?.data?.drafts || (Array.isArray(r?.data) ? r.data : []);
        drafts.slice(0, 2).forEach((d) => {
          const id = d.id || d._id;
          if (!id) return;
          collected.push({
            id: `grievance-${id}`,
            type: 'grievance',
            title: d.grievance_title || 'Untitled grievance draft',
            status: d.status ? String(d.status).replace(/_/g, ' ') : 'Draft',
            updatedAt: d.updated_at || d.updatedAt,
            route: `/grievance-builder/${id}`,
          });
        });
      } catch { /* silent */ }

      if (cancelled) return;

      // Sort by updatedAt desc, cap to 4 items.
      collected.sort((a, b) => {
        const ta = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
        const tb = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
        return tb - ta;
      });
      setItems(collected.slice(0, 4));
    })();
    return () => { cancelled = true; };
  }, []);

  return items;
}
